<template>
  <div class="JournalistRegisterForm">
    <template v-if="!successful">
      <p class="mb-5 font-sans text-sm leading-relaxed text-muted">
        {{ $t('pages.register_journalist.receive_relevant_news') }}
      </p>

      <sign-up-server-errors :server-errors="serverErrors" />

      <form
        class="JournalistRegister"
        @submit.prevent="submitRegister"
      >
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block font-display text-sm font-semibold text-ink">
              {{ $t('forms.first_name') }}
            </label>
            <input
              v-model="formFields.first_name"
              :class="inputClass($v.formFields.first_name)"
              :placeholder="$t('forms.first_name')"
              type="text"
              name="first_name"
              data-testid="JournalistRegister-FirstName"
              @input="$v.formFields.first_name.$touch()"
            >
            <p v-if="$v.formFields.first_name.$error" class="mt-1 text-sm text-brand">
              {{ fieldError($v.formFields.first_name, $t('forms.first_name')) }}
            </p>
          </div>

          <div>
            <label class="mb-1.5 block font-display text-sm font-semibold text-ink">
              {{ $t('forms.last_name') }}
            </label>
            <input
              v-model="formFields.last_name"
              :class="inputClass($v.formFields.last_name)"
              :placeholder="$t('forms.last_name')"
              type="text"
              name="last_name"
              data-testid="JournalistRegister-LastName"
              @input="$v.formFields.last_name.$touch()"
            >
            <p v-if="$v.formFields.last_name.$error" class="mt-1 text-sm text-brand">
              {{ fieldError($v.formFields.last_name, $t('forms.last_name')) }}
            </p>
          </div>
        </div>

        <div class="mt-4">
          <label class="mb-1.5 block font-display text-sm font-semibold text-ink">
            {{ $t('forms.email') }}
          </label>
          <input
            v-model="formFields.email"
            :class="inputClass($v.formFields.email)"
            :placeholder="$t('forms.email')"
            type="email"
            name="email"
            data-testid="JournalistRegister-Email"
            @input="$v.formFields.email.$touch()"
          >
          <p v-if="$v.formFields.email.$error" class="mt-1 text-sm text-brand">
            {{ fieldError($v.formFields.email, $t('forms.email')) }}
          </p>
        </div>

        <div class="mt-4">
          <label class="mb-1.5 block font-display text-sm font-semibold text-ink">
            {{ $t('forms.password') }}
          </label>
          <input
            v-model="formFields.password"
            :class="inputClass($v.formFields.password)"
            :placeholder="$t('forms.password')"
            type="password"
            data-testid="JournalistRegister-Password"
            @input="$v.formFields.password.$touch()"
          >
          <p v-if="$v.formFields.password.$error" class="mt-1 text-sm text-brand">
            {{ fieldError($v.formFields.password, $t('forms.password')) }}
          </p>
        </div>

        <button
          :disabled="isLoading"
          type="submit"
          class="mt-6 w-full cursor-pointer rounded-full border-none bg-accent px-6 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="submit"
          @click="sendGAClickEvent('hypenews signup')"
        >
          {{ $t('forms.sign_up') }}
        </button>
      </form>
    </template>

    <sign-up-success-message v-if="successful" journalist />
  </div>
</template>

<script>
import { required, email, minLength } from 'vuelidate/lib/validators'
import { isPasswordStrongEnough } from '@/shared/js/utils/validation'

import RegisterBase from '@/pages/registrations/RegisterBase'
import fieldErrorMixin from '@/components/dashboard/settings/fieldErrorMixin'
import SignUpServerErrors from '@/components/signup/SignUpServerErrors.vue'
import SignUpSuccessMessage from '@/components/signup/SignUpSuccessMessage.vue'

/**
 * Journalist registration form — the house-styled, de-Bulma'd successor to the old
 * standalone JournalistRegister page (S17). Reparented into the "Create media account"
 * modal on ForJournalists, but self-contained so it carries the full register flow:
 * it {@see module:RegisterBase} for `submitRegister`/`registerRequest`, overrides the
 * post-submit hook to a no-op (journalist sign-up shows the success message in place —
 * no onboarding redirect, matching the old page), and forces `type: 'reader'`.
 *
 * Field validation uses `fieldErrorMixin` (the S16 Vuelidate-without-FormField helpers);
 * validator names are kept mixin-friendly (`email`/`min`/`password.strength`) so the
 * existing `validations.*` messages resolve — the validation behaviour is identical to
 * the old page (required, email format, min length 3, password strength ≥ 6).
 */
export default {
  name: 'JournalistRegisterForm',

  components: {
    SignUpServerErrors,
    SignUpSuccessMessage
  },

  extends: RegisterBase,

  mixins: [fieldErrorMixin],

  validations: {
    formFields: {
      first_name: { required, min: minLength(3) },
      last_name: { required, min: minLength(3) },
      email: { required, email },
      password: { required, 'password.strength': isPasswordStrongEnough(6) }
    }
  },

  data () {
    return {
      formFields: {
        type: 'reader'
      }
    }
  },

  computed: {
    gaEventName: () => 'Journalist sign up'
  },

  methods: {
    // Journalist sign-up shows the success message in place — no onboarding redirect.
    onRegisterSuccess () {}
  }
}
</script>
